import React, { useCallback, useMemo } from "react";
import {
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
  Box,
} from "@material-ui/core";
import { useStyles } from "./notificationCentr.styles";
import { differenceInHours, formatDistanceToNowStrict } from "date-fns";
import { NotificatioResponse } from "./redux/notificationCenterSlice.types";
import { TextConstants } from "constants/TextConstants";

interface NotificationListProps {
  notifications: NotificatioResponse[];
  onMarkAsRead: (id: string) => void;
  onCopyToClipboard: (text: string, id: string) => void;
  tooltipOpen: string | null;
  setTooltipOpen: (id: string | null) => void;
}

// Memoized ListItem Component
const MemoizedNotificationItem = React.memo(
  ({
    notification,
    classes,
    handleMarkAsRead,
    handleCopyToClipboard,
    tooltipOpen,
    setTooltipOpen,
  }: {
    notification: NotificatioResponse & { timeText: string };
    classes: ReturnType<typeof useStyles>;
    handleMarkAsRead: (id: string) => (e: React.MouseEvent) => void;
    handleCopyToClipboard: (
      text: string,
      id: string
    ) => (e: React.MouseEvent) => void;
    tooltipOpen: string | null;
    setTooltipOpen: (id: string | null) => void;
  }) => (
    <React.Fragment key={notification.messageId}>
      <ListItem
        button
        disableRipple
        disableTouchRipple
        className={
          !notification.ack
            ? classes.unreadNotification
            : classes.readNotification
        }
      >
        <ListItemText
          primary={
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Box display="flex" alignItems="center">
                {!notification.ack && (
                  <Tooltip title={TextConstants.MARK_AS_READ} placement="right">
                    <span
                      className={classes.unreadDot}
                      role="button"
                      aria-label={TextConstants.MARK_AS_READ}
                      onClick={handleMarkAsRead(notification.messageId)}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleMarkAsRead(notification.messageId);
                        }
                      }}
                    ></span>
                  </Tooltip>
                )}

                <Tooltip
                  title={
                    tooltipOpen === notification.messageId
                      ? TextConstants.COPIED
                      : TextConstants.COPY_CODE
                  }
                  onClose={() => setTooltipOpen(null)}
                  placement="right"
                >
                  <Typography
                    className={classes.consignmentCode}
                    onClick={handleCopyToClipboard(
                      notification.title,
                      notification.messageId
                    )}
                  >
                    {notification.title}
                  </Typography>
                </Tooltip>
              </Box>
              <Typography className={classes.timeText}>
                {notification.timeText}
              </Typography>
            </Box>
          }
          secondary={
            <Typography className={classes.secondaryText}>
              {notification.subTitle}
            </Typography>
          }
        />
      </ListItem>
      <Divider />
    </React.Fragment>
  )
);

const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  onMarkAsRead,
  onCopyToClipboard,
  tooltipOpen,
  setTooltipOpen,
}) => {
  const classes = useStyles();

  // Memoize time calculations for performance
  const getTimeText = useCallback((updatedAt: string) => {
    const hoursDifference = differenceInHours(new Date(), new Date(updatedAt));
    if (hoursDifference === 24) return TextConstants.DAY_AGO;
    return formatDistanceToNowStrict(new Date(updatedAt), {
      addSuffix: true,
      unit: hoursDifference < 24 ? TextConstants.HOUR : TextConstants.DAY,
    });
  }, []);

  // Precompute notifications with time text
  const processedNotifications = useMemo(
    () =>
      notifications.map((notification) => ({
        ...notification,
        timeText: getTimeText(notification.updatedAt),
      })),
    [notifications, getTimeText]
  );

  const handleMarkAsRead = useCallback(
    (id: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      onMarkAsRead(id);
    },
    [onMarkAsRead]
  );

  const handleCopyToClipboard = useCallback(
    (text: string, id: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      onCopyToClipboard(text, id);
    },
    [onCopyToClipboard]
  );

  return (
    <Box>
      {processedNotifications.length > 0 ? (
        <List>
          {processedNotifications.map((notification) => (
            <MemoizedNotificationItem
              key={notification.messageId}
              notification={notification}
              classes={classes}
              handleMarkAsRead={handleMarkAsRead}
              handleCopyToClipboard={handleCopyToClipboard}
              tooltipOpen={tooltipOpen}
              setTooltipOpen={setTooltipOpen}
            />
          ))}
        </List>
      ) : (
        <Typography className={classes.noData}>
          {TextConstants.NO_NOTIFICATIONS}
        </Typography>
      )}
    </Box>
  );
};

export default NotificationList;
